/**
 * Balance (estado de cuenta) del cliente de un crédito.
 *
 * Puerto de `Credito::balance` + `views/menu/credito/balance.php`, la pantalla
 * que el listado de créditos del panel anterior abría con el botón **Balance**
 * de cada fila: el saldo inicial del periodo, los cargos de sus facturas a
 * crédito, los abonos de sus pagos y el saldo acumulado movimiento a
 * movimiento.
 *
 * Reglas conservadas:
 * - El **saldo inicial** es el `costo_comprobante` de las facturas a crédito
 *   emitidas antes de la fecha inicial (`Credito_model::get_initial_balance`).
 * - Los **cargos** son el total de las facturas del rango y los **abonos** los
 *   movimientos de `movements_account` no anulados.
 * - `Saldo al corte = saldo inicial + cargos − retenciones − abonos`, y el saldo
 *   de cada fila de la tabla acumula el **pendiente** de las facturas menos los
 *   abonos (como el original, que además recorre día a día).
 */

import { BadgeDollarSign, Info } from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';

import { useBalanceCredito } from '../cierre.api';
import type { FiltrosBalance } from '../cierre-gestion.types';
import { fechaCorta, hoyIso, inicioDeMesIso, monto } from '../gestion-formato';

interface Props {
  /** Crédito abierto (`null` cierra el diálogo). */
  id: number | null;
  onClose: () => void;
  /** Abre «Pagos / Liquidación de facturas» del crédito, como «Agregar Pagos». */
  onPagar: (id: number) => void;
}

export function CreditoBalanceDialog({ id, onClose, onPagar }: Props) {
  const [filtros, setFiltros] = useState<FiltrosBalance>(() => ({
    desde: inicioDeMesIso(),
    hasta: hoyIso(),
  }));

  const consulta = useBalanceCredito(id, filtros);
  const datos = consulta.data;

  return (
    <Dialog
      open={id !== null}
      onClose={onClose}
      size="xl"
      title={datos ? `Balance · ${datos.credito.cliente}` : 'Balance'}
      description={
        datos
          ? `${datos.credito.numero_doc} · ${datos.credito.producto} · ${datos.credito.tipo_credito ?? ''}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={id === null}
            leftIcon={<BadgeDollarSign className="size-4" />}
            onClick={() => id !== null && onPagar(id)}
          >
            Agregar pago
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            Imprimir
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Desde"
            type="date"
            value={filtros.desde ?? ''}
            onChange={(evento) =>
              setFiltros((previo) => ({ ...previo, desde: evento.target.value }))
            }
          />
          <Input
            label="Hasta"
            type="date"
            value={filtros.hasta ?? ''}
            onChange={(evento) =>
              setFiltros((previo) => ({ ...previo, hasta: evento.target.value }))
            }
          />
          <div className="flex items-end">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={!id || consulta.isFetching}
              onClick={() => void consulta.refetch()}
            >
              Consultar
            </Button>
          </div>
        </div>

        {consulta.isLoading ? <Skeleton className="h-72 w-full" /> : null}

        {consulta.isError ? (
          <Alert variant="error" title="No se pudo obtener el balance">
            {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
          </Alert>
        ) : null}

        {datos ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-card border border-ink-100 bg-white p-4">
                <p className="mb-2 text-xs text-ink-500">
                  Periodo: {fechaCorta(datos.desde)} – {fechaCorta(datos.hasta)}
                </p>
                <dl className="space-y-1.5 text-sm">
                  <Fila etiqueta="(+) Saldo inicial" valor={monto(datos.resumen.saldo_inicial)} />
                  <Fila etiqueta="(+) Cargos" valor={monto(datos.resumen.cargos)} />
                  {datos.credito.withholding ? (
                    <Fila etiqueta="(−) Retenciones" valor={monto(datos.resumen.retencion)} />
                  ) : null}
                  <Fila etiqueta="(−) Abonos" valor={monto(datos.resumen.abonos)} />
                  <Fila
                    etiqueta="Saldo al corte"
                    valor={monto(datos.resumen.saldo_al_corte)}
                    destacado
                  />
                </dl>
                <p className="mt-2 text-xs text-ink-500">
                  Importes en soles (S/). El vencido del periodo es {monto(datos.resumen.vencido)} y
                  el pagadero {monto(datos.resumen.pagadero)}.
                </p>
              </div>

              <div className="rounded-card border border-ink-100 bg-ink-50/40 p-4 text-sm text-ink-700">
                <p className="font-semibold text-ink-800">{datos.credito.cliente}</p>
                <p className="text-xs text-ink-500">{datos.credito.numero_doc}</p>
                {datos.credito.direccion ? <p className="mt-2">{datos.credito.direccion}</p> : null}
                <p className="mt-1 text-xs text-ink-500">
                  {[datos.credito.telefono, datos.credito.correo].filter(Boolean).join(' · ') ||
                    'Sin contacto registrado'}
                </p>
                <p className="mt-3 text-xs text-ink-500">
                  Línea {monto(datos.credito.linea)} ({monto(datos.credito.limite)} +{' '}
                  {monto(datos.credito.flexibilidad)} de flexibilidad)
                </p>
              </div>
            </div>

            <div className="max-h-[40vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-3 py-2 whitespace-nowrap">Emisión</th>
                    <th className="px-3 py-2 whitespace-nowrap">Comprobante</th>
                    <th className="px-3 py-2">Información</th>
                    <th className="px-3 py-2 whitespace-nowrap">Vence</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
                    {datos.credito.withholding ? (
                      <th className="px-3 py-2 text-right whitespace-nowrap">Retención</th>
                    ) : null}
                    <th className="px-3 py-2 text-right whitespace-nowrap">Pago</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-ink-50/60 text-ink-600">
                    <td className="px-3 py-2" colSpan={datos.credito.withholding ? 6 : 5}>
                      Saldo del periodo anterior
                    </td>
                    <td className="px-3 py-2 text-right">—</td>
                    <td className="px-3 py-2 text-right font-semibold text-ink-800">
                      {monto(datos.resumen.saldo_inicial)}
                    </td>
                  </tr>

                  {datos.movimientos.map((movimiento) => (
                    <tr
                      key={`${movimiento.tipo}-${movimiento.fecha}-${movimiento.comprobante ?? movimiento.pago}-${movimiento.saldo}`}
                      className="border-t border-ink-100"
                    >
                      <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(movimiento.fecha)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{movimiento.comprobante ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span
                          className="line-clamp-2"
                          title={movimiento.informacion ?? undefined}
                        >
                          {movimiento.informacion ?? (movimiento.tipo === 'abono' ? 'Pago' : '—')}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {movimiento.vencimiento ? fechaCorta(movimiento.vencimiento) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {movimiento.total ? monto(movimiento.total) : '—'}
                      </td>
                      {datos.credito.withholding ? (
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {movimiento.retencion ? monto(movimiento.retencion) : '—'}
                        </td>
                      ) : null}
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {movimiento.pago ? monto(movimiento.pago) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold whitespace-nowrap text-ink-800">
                        {monto(movimiento.saldo)}
                      </td>
                    </tr>
                  ))}

                  {datos.movimientos.length === 0 ? (
                    <tr>
                      <td
                        className="px-3 py-6 text-center text-ink-500"
                        colSpan={datos.credito.withholding ? 8 : 7}
                      >
                        Sin movimientos en el periodo.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <Alert variant="info" title="Cómo leerlo">
              <span className={ICONO_EN_ALERTA}>
                <Info className="size-4" aria-hidden="true" />
                Los cargos son el <b>total</b> de las facturas del periodo (el listado de Créditos
                muestra el pendiente), y el saldo de cada fila acumula el pendiente menos los abonos,
                como el panel anterior. Con «Agregar pago» se registran los pagos de esas facturas;
                aprobar facturas sigue en el panel anterior.
              </span>
            </Alert>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Fila de la tabla de resumen. */
function Fila({
  etiqueta,
  valor,
  destacado = false,
}: {
  etiqueta: string;
  valor: string;
  destacado?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={destacado ? 'font-semibold text-ink-800' : 'text-ink-600'}>{etiqueta}</dt>
      <dd className={destacado ? 'font-bold text-ink-900' : 'font-medium text-ink-800'}>{valor}</dd>
    </div>
  );
}
